import React, { useState, useEffect } from 'react';
import WizardSteps from '../common/WizardSteps';
import { mockProducts, mockCustomers, getProjectsByCustomer, getRecentlyUsedProducts, getRecentlyUsedCustomers } from '../../data/mockData';
import { useScaleData } from '../../hooks/useScaleData';

const CreateTicketScreen = ({ state, setState, devices, user, company, onNavigate, config, deviceCode }) => {
    const [searchTerms, setSearchTerms] = useState({
        products: '',
        customers: '',
        projects: ''
    });
    const [configuredCameras, setConfiguredCameras] = useState([]);
    const [primaryCamera, setPrimaryCamera] = useState(null);
    const [products, setProducts] = useState([]);
    const [productsLoading, setProductsLoading] = useState(false);
    const [categories, setCategories] = useState([]);
    const [units, setUnits] = useState([]);
    const [trucktypes, setTrucktypes] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [projects, setProjects] = useState([]);
    const [categoriesLoading, setCategoriesLoading] = useState(false);
    const [unitsLoading, setUnitsLoading] = useState(false);
    const [trucktypesLoading, setTrucktypesLoading] = useState(false);
    const [customersLoading, setCustomersLoading] = useState(false);
    const [projectsLoading, setProjectsLoading] = useState(false);
    const [recentlyUsedCustomers, setRecentlyUsedCustomers] = useState([]);
    const [recentlyUsedProducts, setRecentlyUsedProducts] = useState([]);
    const [isUpdatingTruck, setIsUpdatingTruck] = useState(false);
    const [plateManuallyChanged, setPlateManuallyChanged] = useState(false);
    const [isOnline, setIsOnline] = useState(true);
    const [usingCachedData, setUsingCachedData] = useState(false);
    
    // Mutex to prevent multiple simultaneous ticket number generation
    const [isGeneratingTicketNumber, setIsGeneratingTicketNumber] = useState(false);

    const { 
		currentWeight, 
		weightStatus,
        setupScaleDataListener, 
		cleanupScaleDataListener,
		// Dual scale support
		isDualScaleMode,
		emptyScaleWeight,
		emptyScaleStatus,
		fullScaleWeight,
		fullScaleStatus
	} = useScaleData(config?.scaleType, config);

    // Determine which scale to use based on truck state
    const getCurrentScaleData = () => {
        if (!isDualScaleMode) {
            // Single scale mode - use the main scale data
            return {
                weight: currentWeight,
                status: weightStatus
            };
        } else {
            // Dual scale mode - for ticket creation, we typically want to use the full scale
            // since we're weighing loaded trucks at the exit scale.
            
            // Default to full scale (exit scale) for ticket creation
            // This represents the most common use case where we're weighing loaded trucks
            return {
                weight: fullScaleWeight,
                status: fullScaleStatus,
                scaleType: 'full'
            };
        }
    };

    const currentScaleData = getCurrentScaleData();

    // Set up scale data listener when scale connection state changes
	useEffect(() => {
		if (devices.scale.connected) {
			// console.log('🏠 HomeScreen: Scale connected via devices prop');
			setupScaleDataListener();
		} else {
			// console.log('🏠 HomeScreen: Scale disconnected via devices prop');
			cleanupScaleDataListener();
		}
	}, [devices.scale.connected, setupScaleDataListener, cleanupScaleDataListener]);

    // Check network status on mount and periodically
    useEffect(() => {
        const checkNetwork = async () => {
            if (window.electronAPI && window.electronAPI.checkNetworkStatus) {
                const status = await window.electronAPI.checkNetworkStatus();
                setIsOnline(status.online);
            }
        };

        checkNetwork();
        const interval = setInterval(checkNetwork, 30000); // Every 30 seconds

        // Listen for truck sync events to refresh cache
        if (window.electronAPI && window.electronAPI.onMessage) {
            const handleTruckSynced = () => {
                // console.log('🔄 Truck synced - refreshing truck cache');
                fetchRegisteredTrucks();
            };

            window.electronAPI.onMessage('truck-synced', handleTruckSynced);
        }

        return () => clearInterval(interval);
    }, []);

    // Set up scale data listener and camera streams when component mounts
    useEffect(() => {

        // Set up configured cameras
        if (devices.cameras && devices.cameras.length > 0) {
			// console.log('🏠 HomeScreen: Setting up camera streams');

			let configured = [];

			// Start camera streams for all configured cameras
			devices.cameras.forEach((camera, index) => {
				if (camera.mjpegUrl && window.camera && window.camera.start) {
					// console.log(`🏠 HomeScreen: Starting camera ${index}: ${camera.name}`);

					// Set up camera frame listener BEFORE starting the camera
					window.camera.onFrame(camera.id, (jpegBase64) => {
						// // console.log(`🏠 HomeScreen: Received frame from camera ${camera.id}`);
						const cameraElements = document.querySelectorAll(`[data-camera-id="${camera.id}"]`);
						cameraElements.forEach(el => {
							el.src = `data:image/jpeg;base64,${jpegBase64}`;
						});
					});

					// Start the camera stream
					window.camera.start(
						camera.mjpegUrl,
						camera.username || '',
						camera.password || '',
						camera.id,
						camera.authType || 'none'
					);
					if (camera.isPrimary) {
						setPrimaryCamera(camera);
					} else {
						configured.push(camera);
					}
				}
			});

			if (configured) {
				setConfiguredCameras(configured);
			}
		} else {
			// Clear cameras when devices.cameras is empty or undefined
			setPrimaryCamera(null);
			setConfiguredCameras([]);
		}

        // Cleanup function to remove event listeners and stop cameras
        return () => {
            if (window.electronAPI && window.electronAPI.removeAllListeners) {
                // console.log('🎫 CreateTicketScreen: Cleaning up scale data listener');
                window.electronAPI.removeAllListeners('scale-data');
            }
            
            // Stop camera streams when component unmounts
            if (configuredCameras.length > 0 && window.camera && window.camera.stop) {
                configuredCameras.forEach(camera => {
                    // console.log('🎫 CreateTicketScreen: Stopping camera stream:', camera.name);
                    window.camera.stop(camera.id);
                });
            }
        };
    }, [devices.scale.connected, devices.cameras]);

    // Fetch products, categories, units, and customers when component mounts
    useEffect(() => {
        fetchProducts();
        fetchCategories();
        fetchUnits();
        fetchTruckTypes();
        fetchCustomers();
        fetchRegisteredTrucks(); // Cache ALL trucks for offline lookup
    }, []);

    // Refresh truck cache when coming back online
    useEffect(() => {
        if (isOnline) {
            // console.log('🌐 Back online - refreshing truck cache');
            fetchRegisteredTrucks();
        }
    }, [isOnline]);

    useEffect(() => {
        if (trucktypes.length > 0 && !state.truckType) {
            setState(prev => ({
                ...prev,
                truckType: trucktypes[0].Id
            }));
        }
    }, [trucktypes, state.truckType]);

    const steps = ['Capture Images', 'Select Product', 'Select Customer', 'Select Project', 'Confirm & Finalize'];

    const captureImages = async () => {
        try {
            // OFFLINE MODE - Still allow camera capture, cameras work locally
            if (!isOnline) {
                // console.log('📴 Offline mode - proceeding with local camera capture');
            }

            // Capture images from all configured cameras
            const capturedImages = [];

            let allCameras = [primaryCamera, ...configuredCameras].filter(camera => camera); // Filter out null/undefined cameras
            for (const camera of allCameras) {
                const cameraElement = document.querySelector(`[data-camera-id="${camera.id}"]`);
                if (cameraElement) {
                    const srcAttr = cameraElement.getAttribute('src'); // get the raw attribute value
                    if (srcAttr && srcAttr !== '') {
                        capturedImages.push({
                            id: camera.id,
                            camera: camera.name || `Camera ${camera.id}`,
                            url: cameraElement.src,
                            timestamp: new Date().toISOString(),
                            isPrimary: camera.isPrimary || false
                        });
                        // console.log(`📸 Captured image from camera: ${camera.name} (Primary: ${camera.isPrimary})`);
                    } else {
                        console.warn(`⚠️ Camera ${camera.name} feed not available for capture`);
                    }
                }
            }

            if (capturedImages.length === 0 && allCameras.length > 0) {
                alert('No camera feeds available for capture. Please wait for cameras to load or check your camera configuration.');
                return;
            }


            // Get company data from secure storage
            const companyData = await window.electronAPI.secureStorage.getCompany();
            
            if (!companyData || !companyData.Id) {
                console.error('❌ No company data available');
                alert('Company and location must be configured before creating tickets. Please go to Configuration and select a company and location.');
                return;
            }

            // Handle currentWeight whether it's a string or number
            let numericWeight = 0;
            if (currentScaleData.weight) {
                if (typeof currentScaleData.weight === 'string') {
                    numericWeight = parseFloat(currentScaleData.weight.replace(/[^\d.-]/g, '')) || 0;
                } else {
                    numericWeight = parseFloat(currentScaleData.weight) || 0;
                }
            }

            // Immediately transition to confirm screen with loading state
                setState({
                    ...state,
                    step: 2,
                    images: capturedImages,
                detectedPlate: '',
                tareWeight: numericWeight || 0,
                currentWeight: currentScaleData.weight,
                ticketId: null,
                ticketNum: null,
                registeredTruckId: null,
                isProcessing: true // Add loading state
            });

            // OFFLINE MODE - Skip API ticket creation, proceed directly to product selection
            if (!isOnline) {
                // console.log('📴 Offline mode - skipping API ticket creation, proceeding to product selection');
                
                // Update state to proceed to product selection
                setState(prev => ({
                    ...prev,
                    step: 2,
                    images: capturedImages,
                    detectedPlate: '',
                    tareWeight: numericWeight || 0,
                    currentWeight: currentScaleData.weight,
                    ticketId: null, // No ticket ID in offline mode
                    ticketNum: null,
                    registeredTruckId: null,
                    isProcessing: false
                }));
                return;
            }

            // ONLINE MODE - Send images to API for ticket creation and license plate analysis
            const ticketData = {
                images: capturedImages.map(img => ({
                    cameraId: img.id,
                    cameraName: img.camera,
                    imageData: img.url, // Base64 image data
                    timestamp: img.timestamp,
                    isPrimary: img.isPrimary,
                    cameraType: img.isPrimary ? 'primary' : 'secondary'
                })),
                weight: numericWeight,
                companyId: companyData.Id
            };

            // console.log('📤 Creating ticket with images:', ticketData);

            try {
                const ticketResponse = await window.electronAPI.request(
                    `/companies/${companyData.Id}/tickets`,
                    'POST',
                    ticketData,
                    {
                        'Accept': 'application/json',
                        'Content-Type': 'application/json',
                    }
                );

                let ticket = null;
                let detectedPlate = '';
                let tareWeight = numericWeight;
                let truckData = null;

                if (ticketResponse.ok) {
                    const responseData = await ticketResponse.body;
                    // console.log('✅ Ticket created successfully:', responseData);
                    // console.log('🔍 Truck data from response:', responseData?.RegisteredTruck);
                    
                    ticket = responseData;
                    truckData = responseData?.RegisteredTruck;
                    
                    // Update local cache with the new ticket number from the API
                    try {
                        const latestMaxTicketNum = await fetchLatestMaxTicketNum();
                        if (latestMaxTicketNum !== null) {
                            const nextTicketKey = `nextTicketNum_${deviceCode}`;
                            const settings = await window.electronAPI.secureStorage.getSettings() || {};
                            const currentCachedNum = settings[nextTicketKey];
                            const newNextNum = latestMaxTicketNum + 1;
                            
                            // Update to the latest API number if it's higher than our cached number
                            if (!currentCachedNum || newNextNum > currentCachedNum) {
                                settings[nextTicketKey] = newNextNum;
                                await window.electronAPI.secureStorage.setSettings(settings);
                                // console.log(`🔄 Updated local cache after ticket creation: ${currentCachedNum || 'none'} → ${newNextNum} (from API MaxTicketNum: ${latestMaxTicketNum})`);
                            }
                        }
                    } catch (error) {
                        console.warn('⚠️ Failed to update local ticket number cache after creation:', error);
                    }
                    
                    // If truck was found, use its tare weight
                    if (truckData) {
                        detectedPlate = truckData?.NumberPlate || '';
                        tareWeight = truckData?.TareWeight || numericWeight;
                        // console.log('🚛 Truck found - Plate:', detectedPlate, 'Tare:', tareWeight, 'ID:', truckData?.Id);
                    } else {
                        // console.log('❌ No truck data in API response');
                        
                        // Check local pending trucks if no truck found from API
                        if (window.electronAPI && window.electronAPI.secureStorage) {
                            const pendingTrucks = await window.electronAPI.secureStorage.getPendingTrucks();
                            // console.log('🔍 Checking local trucks:', pendingTrucks);
                            
                            if (pendingTrucks && pendingTrucks.length > 0) {
                                // Try to match by plate from OCR (if we add OCR response here in future)
                                // For now, user will need to enter manually or we can show available local trucks
                                // console.log(`📋 Found ${pendingTrucks.length} local trucks available`);
                            }
                        }
                    }
                    
                    // Update state with detected data
                    setState(prev => ({
                        ...prev,
                        detectedPlate: detectedPlate,
                tareWeight: tareWeight,
                        ticketId: ticket?.Id,
                        ticketNum: ticket?.TicketNum,
                        registeredTruckId: truckData?.Id || ticket?.RegisteredTruckId,
                        registeredTruckPlate: truckData?.NumberPlate,
                        registeredTruckNumber: truckData?.TruckNumber,
                        isProcessing: false // Remove loading state
                    }));

                    // Reset manually changed flag if truck was found
                    if (truckData?.Id) {
                        setPlateManuallyChanged(false);
                    }
                } else {
                    console.error('❌ Failed to create ticket:', ticketResponse.statusText);
                    alert('Failed to create ticket. Please try again.');
                    // Reset to step 1 on failure
                    setState(prev => ({
                        ...prev,
                        step: 1,
                        isProcessing: false
                    }));
                }
            } catch (error) {
                console.error('❌ Error during ticket creation:', error);
                alert('Failed to create ticket. Please try again.');
                // Reset to step 1 on error
                setState(prev => ({
                    ...prev,
                    step: 1,
                    isProcessing: false
                }));
            }
        } catch (error) {
            console.error('Failed to capture images:', error);
            alert('Failed to capture images. Please try again.');
        }
    };

    const selectProduct = (product) => {
        setState({
            ...state,
            selectedProduct: product,
            step: 3
        });
    };

    const selectCustomer = (customer) => {
        setState({
            ...state,
            selectedCustomer: customer,
            step: 4
        });
        
        // Clear existing projects and fetch new ones for the selected customer
        setProjects([]);
        fetchProjects(customer.Id);
    };

    const selectProject = (project) => {
        const netWeight = Math.max(0, state.currentWeight - state.tareWeight);
        setState({
            ...state,
            selectedProject: project,
            step: 5,
            netWeight
        });
    };

    const updateTruckInfo = async () => {
        try {
            if (!state.detectedPlate.trim()) {
                alert('Please enter a license plate number first');
                return;
            }

            setIsUpdatingTruck(true);

            // Get company data from secure storage
            const companyData = await window.electronAPI.secureStorage.getCompany();
            
            if (!companyData || !companyData.Id) {
                console.error('❌ No company data available');
                alert('Company and location must be configured before creating tickets. Please go to Configuration and select a company and location.');
                setIsUpdatingTruck(false);
                return;
            }

            // console.log('🔍 Fetching truck info for plate:', state.detectedPlate);

            // First, check cached trucks (includes both API and pending trucks)
            let foundInCache = false;
            const cachedTrucks = await window.electronAPI.secureStorage.getCachedRegisteredTrucks();
            const cachedTruck = cachedTrucks.data.find(
                t => t.NumberPlate && t.NumberPlate.toLowerCase() === state.detectedPlate.toLowerCase()
            );

            if (cachedTruck) {
                // console.log('📦 Truck found in cache:', cachedTruck);
                
                setState(prev => ({
                    ...prev,
                    registeredTruckId: cachedTruck.Id,
                    registeredTruckPlate: cachedTruck.NumberPlate,
                    registeredTruckNumber: cachedTruck.TruckNumber,
                    tareWeight: cachedTruck.TareWeight || prev.tareWeight
                }));

                setPlateManuallyChanged(false);
                foundInCache = true;
                
                if (!isOnline) {
                    alert(`Truck found in cache (offline mode).\n\nPlate: ${cachedTruck.NumberPlate}\nTare Weight: ${cachedTruck.TareWeight}`);
                }
            }

            // Try to fetch from API if online
            if (isOnline) {
                try {
                    // First try the specific truck endpoint (more efficient)
            const truckResponse = await window.electronAPI.request(
                `/companies/${companyData.Id}/registeredtrucks/${state.detectedPlate}`,
                'GET',
                null,
                {
                    'Accept': 'application/json',
                }
            );

            if (truckResponse.ok) {
                const truckData = await truckResponse.body;
                        // console.log('✅ Truck info fetched successfully from API:', truckData);
                
                // Update state with truck information
                setState(prev => ({
                    ...prev,
                    registeredTruckId: truckData.Id,
                    registeredTruckPlate: truckData.NumberPlate,
                    registeredTruckNumber: truckData.TruckNumber,
                    tareWeight: truckData.TareWeight || prev.tareWeight
                }));

                // Reset the manually changed flag since we found a valid truck
                setPlateManuallyChanged(false);
                        
                        // Update cache with this specific truck
                        if (!cachedTrucks.data.find(t => t.Id === truckData.Id)) {
                            const updatedCache = [...cachedTrucks.data, truckData];
                            await window.electronAPI.secureStorage.setCachedRegisteredTrucks(updatedCache);
                        }
                        
                        foundInCache = true;
            } else {
                        // Specific truck not found, fall back to fetching all trucks (for offline mode compatibility)
                        // console.log('❌ Specific truck not found, fetching all trucks...');
                        
                        const trucksResponse = await window.electronAPI.request(
                            `/companies/${companyData.Id}/registeredtrucks`,
                            'GET',
                            null,
                            {
                                'Accept': 'application/json',
                            }
                        );

                        if (trucksResponse.ok) {
                            const trucksData = await trucksResponse.body;
                            const actualTrucks = trucksData.body || trucksData;
                            
                            // Search for the truck by license plate
                            const truckData = actualTrucks.find(
                                t => t.NumberPlate && t.NumberPlate.toLowerCase() === state.detectedPlate.toLowerCase()
                            );
                            
                            if (truckData) {
                                // console.log('✅ Truck found in full truck list:', truckData);
                                
                                // Update state with truck information
                                setState(prev => ({
                                    ...prev,
                                    registeredTruckId: truckData.Id,
                                    registeredTruckPlate: truckData.NumberPlate,
                                    registeredTruckNumber: truckData.TruckNumber,
                                    tareWeight: truckData.TareWeight || prev.tareWeight
                                }));

                                // Reset the manually changed flag since we found a valid truck
                                setPlateManuallyChanged(false);
                                
                                // Update cache with all trucks (refresh the cache)
                                const pendingTrucks = await window.electronAPI.secureStorage.getPendingTrucks();
                                const pendingAsTrucks = pendingTrucks.map(t => ({
                                    Id: t.localId,
                                    NumberPlate: t.numberplate,
                                    TruckNumber: t.trucknumber,
                                    TareWeight: t.tareweight,
                                    DOTNumber: t.dotnumber,
                                    CompanyName: t.companyname,
                                    Status: 'pending_sync'
                                }));
                                const allTrucks = [...actualTrucks, ...pendingAsTrucks];
                                await window.electronAPI.secureStorage.setCachedRegisteredTrucks(allTrucks);
                                
                                foundInCache = true;
                            } else if (!foundInCache) {
                                // Not in API or cache, check local pending trucks
                                // console.log('❌ Truck not found in API, checking pending trucks...');
                                
                                const pendingTrucks = await window.electronAPI.secureStorage.getPendingTrucks();
                                const localTruck = pendingTrucks.find(
                                    t => t.numberplate && t.numberplate.toLowerCase() === state.detectedPlate.toLowerCase()
                                );
                                
                                if (localTruck) {
                                    // console.log('✅ Truck found in pending queue:', localTruck);
                                    
                                    setState(prev => ({
                                        ...prev,
                                        registeredTruckId: localTruck.localId,
                                        registeredTruckPlate: localTruck.numberplate,
                                        registeredTruckNumber: localTruck.trucknumber,
                                        tareWeight: localTruck.tareweight || prev.tareWeight
                                    }));

                                    setPlateManuallyChanged(false);
                                    alert(`Truck found in pending queue (not yet synced).\n\nPlate: ${localTruck.numberplate}\nTare Weight: ${localTruck.tareweight}`);
                                } else {
                                    console.error('❌ Truck not found in API, cache, or pending queue');
                                    alert('No truck found with this license plate. Please register this truck first or check the plate number.');
                                    
                setState(prev => ({
                    ...prev,
                    registeredTruckId: null,
                    registeredTruckPlate: null,
                    registeredTruckNumber: null
                }));
                                }
                            }
                        }
                    }
                } catch (error) {
                    console.error('❌ Error fetching from API:', error);
                    if (!foundInCache) {
                        // Check pending trucks as fallback
                        const pendingTrucks = await window.electronAPI.secureStorage.getPendingTrucks();
                        const localTruck = pendingTrucks.find(
                            t => t.numberplate && t.numberplate.toLowerCase() === state.detectedPlate.toLowerCase()
                        );
                        
                        if (localTruck) {
                            setState(prev => ({
                                ...prev,
                                registeredTruckId: localTruck.localId,
                                registeredTruckPlate: localTruck.numberplate,
                                registeredTruckNumber: localTruck.trucknumber,
                                tareWeight: localTruck.tareweight || prev.tareWeight
                            }));

                            setPlateManuallyChanged(false);
                            alert(`Truck found in pending queue.\n\nPlate: ${localTruck.numberplate}\nTare Weight: ${localTruck.tareweight}`);
                        }
                    }
                }
            } else if (!foundInCache) {
                // Offline and not in cache, check pending trucks
                // console.log('📴 Offline - checking pending trucks');
                
                const pendingTrucks = await window.electronAPI.secureStorage.getPendingTrucks();
                const localTruck = pendingTrucks.find(
                    t => t.numberplate && t.numberplate.toLowerCase() === state.detectedPlate.toLowerCase()
                );
                
                if (localTruck) {
                    // console.log('✅ Truck found in pending queue:', localTruck);
                    
                    setState(prev => ({
                        ...prev,
                        registeredTruckId: localTruck.localId,
                        registeredTruckPlate: localTruck.numberplate,
                        registeredTruckNumber: localTruck.trucknumber,
                        tareWeight: localTruck.tareweight || prev.tareWeight
                    }));

                    setPlateManuallyChanged(false);
                    alert(`Truck found in pending queue (offline).\n\nPlate: ${localTruck.numberplate}\nTare Weight: ${localTruck.tareweight}`);
                } else {
                    console.error('❌ Truck not found offline');
                    alert('No truck found with this license plate. Please register this truck first.');
                    
                    setState(prev => ({
                        ...prev,
                        registeredTruckId: null,
                        registeredTruckPlate: null,
                        registeredTruckNumber: null
                    }));
                }
            }
        } catch (error) {
            console.error('❌ Error updating truck info:', error);
            alert('Failed to update truck info. Please try again.');
        } finally {
            setIsUpdatingTruck(false);
        }
    };

    // Helper function to fetch the latest MaxTicketNum from API
    const fetchLatestMaxTicketNum = async () => {
        try {
            const companyData = await window.electronAPI.secureStorage.getCompany();
            if (!companyData || !companyData.Id) {
                return null;
            }

            const companyResponse = await window.electronAPI.request(
                `/companies/${companyData.Id}`,
                'GET',
                null,
                {
                    'Accept': 'application/json',
                }
            );

            if (companyResponse.ok) {
                const companyInfo = await companyResponse.body;
                const maxTicketNum = companyInfo.MaxTicketNum || 0;
                // console.log(`📡 Fetched latest MaxTicketNum from API: ${maxTicketNum}`);
                return maxTicketNum;
            }
            return null;
        } catch (error) {
            console.warn('⚠️ Error fetching MaxTicketNum from API:', error);
            return null;
        }
    };

    // Helper function to get and increment the next ticket number for offline tickets
    const generateLocalTicketNumber = async () => {
        // Prevent multiple simultaneous calls
        if (isGeneratingTicketNumber) {
            // console.log('⏳ Ticket number generation already in progress, waiting...');
            // Wait a bit and try again
            await new Promise(resolve => setTimeout(resolve, 100));
            return generateLocalTicketNumber();
        }
        
        setIsGeneratingTicketNumber(true);
        
        try {
            // Get company data from secure storage
            const companyData = await window.electronAPI.secureStorage.getCompany();
            if (!companyData || !companyData.Id) {
                console.error('❌ No company data available for ticket numbering');
                throw new Error('No company data available');
            }

            // Check if we have a cached next ticket number for this device
            const nextTicketKey = `nextTicketNum_${deviceCode}`;
            const lastFetchKey = `lastMaxTicketFetch_${deviceCode}`;
            const settings = await window.electronAPI.secureStorage.getSettings() || {};
            let nextTicketNum = settings[nextTicketKey];
            let lastFetchTime = settings[lastFetchKey];

            // Check if we need to refresh the MaxTicketNum (every 30 seconds or if no cache)
            const now = Date.now();
            const shouldRefresh = !lastFetchTime || (now - lastFetchTime) > 30000; // 30 seconds

            if (shouldRefresh || !nextTicketNum) {
                // console.log('📡 Refreshing MaxTicketNum from API...');
                
                const latestMaxTicketNum = await fetchLatestMaxTicketNum();
                
                if (latestMaxTicketNum !== null) {
                    // Use the latest MaxTicketNum + 1, or our cached number if it's higher
                    const apiNextNum = latestMaxTicketNum + 1;
                    nextTicketNum = nextTicketNum ? Math.max(nextTicketNum, apiNextNum) : apiNextNum;
                    
                    // console.log(`✅ Updated to latest MaxTicketNum: ${latestMaxTicketNum}, using next: ${nextTicketNum}`);
                    
                    // Update the last fetch time
                    settings[lastFetchKey] = now;
                    await window.electronAPI.secureStorage.setSettings(settings);
                } else {
                    // API fetch failed, but we should still use our cached number if we have one
                    if (nextTicketNum) {
                        // console.log(`📋 API fetch failed, using existing cached number: ${nextTicketNum}`);
                    } else {
                        console.warn('⚠️ Failed to fetch company info and no cached number, using fallback numbering');
                        // Fallback to timestamp-based numbering only if we have no cached number
                        const time = now.toString().slice(-6);
                        nextTicketNum = parseInt(time);
                    }
                }
            } else {
                // console.log(`📋 Using cached next ticket number: ${nextTicketNum} (cached ${Math.round((now - lastFetchTime) / 1000)}s ago)`);
            }

            // Generate the ticket number with device code suffix
            const ticketNumber = `${nextTicketNum}-${deviceCode}`;
            
            // Increment and save the next ticket number for this device IMMEDIATELY
            // This prevents race conditions if the function is called multiple times
            const incrementedNum = nextTicketNum + 1;
            settings[nextTicketKey] = incrementedNum;
            await window.electronAPI.secureStorage.setSettings(settings);
            
            // console.log(`🎫 Generated ticket number: ${ticketNumber} (next will be: ${incrementedNum})`);
            return ticketNumber;

        } catch (error) {
            console.error('❌ Error generating ticket number:', error);
            // Fallback to timestamp-based numbering
            const now = new Date();
            const time = now.getTime().toString().slice(-6);
            return `${time}${deviceCode}`;
        } finally {
            setIsGeneratingTicketNumber(false);
        }
    };

    // Helper function to print ticket
    const printTicket = async (ticketNum, isOffline = false) => {
                try {
                    // Get printer configuration from devices
                    if (!devices.printer.connected || !devices.printer.name) {
                        console.warn('⚠️ No printer configured or connected');
                const message = isOffline ? 
                    'Ticket saved locally! No printer configured.' : 
                    'Ticket created successfully! No printer configured for automatic printing.';
                alert(message);
                return false;
                    }

                    // Format ticket data for printing
                    const currentDate = new Date();
                    const formattedDate = currentDate.toLocaleDateString();
                    const formattedTime = currentDate.toLocaleTimeString();
                    
                    // Create professional ticket format
                    const ticketReceipt = [
                        company?.Name || 'COMPANY NAME',
                        `${state.selectedProduct?.LocationName || ''}`,
                        `<br />`,
                `Ticket #: ${ticketNum}`,
                        `<br />`,
                        `${formattedDate} ${formattedTime}`,
                        `<br />`,
                        `Product: ${state.selectedProduct?.Name || 'N/A'}`,
                        `Quantity: ${state.netWeight.toLocaleString()} ${getUnitName(state.selectedProduct?.UnitId)}s`,
                        `<br />`,
                        `Customer: ${state.selectedCustomer?.Name || 'N/A'}`,
                        `PO #: ${state.poNumber?.trim() || 'N/A'}`,
                        `<br />`,
                        `Truck #: ${state.registeredTruckNumber || state.registeredTruckPlate || 'N/A'}`,
                        `License Plate: ${state.registeredTruckPlate || 'N/A'}`,
                        `<br />`,
                        `Tare Weight: ${state.tareWeight || 'N/A'}`,
                        `Gross Weight: ${state.currentWeight || 'N/A'}`,
                        `Net Weight: ${state.netWeight || 'N/A'}`,
                        `<br />`,
                        `<br />`,
                // Add offline indicator if needed
                ...(isOffline ? ['*** OFFLINE TICKET ***', 'Will sync when online'] : [])
                    ];
        
            const printResult = await window.printerAPI.printJob({
                        printerName: devices.printer.name,
                        paperWidth: devices.printer.paperWidth,
                ticketNumber: ticketNum,
                        font: "A",
                        align: "center",
                        style: "b",
                        size: [2, 2],
                        text: ticketReceipt,
                    });
        
            // console.log('✅ Ticket printed successfully');
            return true;
                } catch (error) {
                    console.error('❌ Print failed:', error);
            const message = isOffline ? 
                'Ticket saved locally but printing failed. Please check your printer connection.' :
                'Ticket created but printing failed. Please check your printer connection.';
            alert(message);
            return false;
        }
    };

    const finalizeTicket = async () => {
        try {
            // Validate required data
            if (!state.detectedPlate.trim()) {
                alert('Please enter a valid license plate number');
                return;
            }

            if (state.netWeight <= 0) {
                alert('Net weight must be greater than zero');
                return;
            } else if(state.netWeight > 60) {
                alert('Net weight cannot exceed 60 Tons');
                return;
            }

            // Get company data from secure storage
            const companyData = await window.electronAPI.secureStorage.getCompany();
            
            if (!companyData || !companyData.Id) {
                console.error('❌ No company data available');
                alert('Company and location must be configured before creating tickets. Please go to Configuration and select a company and location.');
                return;
            }

            // Prepare ticket data
            const finalTicketData = {
                RegisteredTruckId: state.registeredTruckId,
                RegisteredTruckPlate: state.registeredTruckPlate,
                RegisteredTruckNumber: state.registeredTruckNumber,
                ProductId: state.selectedProduct?.Id,
                CustomerId: state.selectedCustomer?.Id,
                ProjectId: state.selectedProject?.Id,
                PONum: state.poNumber?.trim() || null,
                TruckTypeId: state.truckType,
                Quantities: [state.netWeight],
                UnitId: state.selectedProduct?.UnitId,
                Weights: {
                    tare: state.tareWeight,
                    gross: state.currentWeight,
                    net: state.netWeight
                },
                FinalWeight: state.currentWeight,
                NetWeight: state.netWeight,
                Date: new Date().toISOString(),
                IsDraft: false,
                Active: 1,
                companyId: companyData.Id,
                // Store full objects for offline display
                Product: state.selectedProduct,
                Customer: state.selectedCustomer,
                Project: state.selectedProject,
                // Include images - cameras work locally in offline mode
                // Format images the same way as online mode for API compatibility
                images: (state.images || []).map(img => ({
                    cameraId: img.id,
                    cameraName: img.camera,
                    imageData: img.url, // Base64 image data (data:image/jpeg;base64,...)
                    timestamp: img.timestamp,
                    isPrimary: img.isPrimary,
                    cameraType: img.isPrimary ? 'primary' : 'secondary'
                }))
            };

            // OFFLINE MODE - Print first, then save locally
            if (!isOnline) {
                // console.log(`📴 Offline mode - printing and saving ticket locally with ${state.images?.length || 0} images`);
                
                // Generate local ticket number for printing
                const localTicketNum = await generateLocalTicketNumber();
                // console.log(`📄 Generated local ticket number: ${localTicketNum}`);
                
                // Print ticket immediately
                const printSuccess = await printTicket(localTicketNum, true);
                
                // Add ticket number to data before saving
                const ticketDataWithNumber = {
                    ...finalTicketData,
                    ticketNum: localTicketNum
                };
                
                const savedTicket = await window.electronAPI.secureStorage.addPendingTicket(ticketDataWithNumber);
                
                if (savedTicket) {
                    const printStatus = printSuccess ? 'and printed successfully!' : 'but printing failed.';
                    const imageCount = state.images?.length || 0;
                    const imageNote = imageCount > 0 ? `${imageCount} image(s) included.` : 'No images captured.';
                    alert(`Ticket saved locally ${printStatus}\n\nIt will be synced to the server when internet connection is restored.\n\nNote: ${imageNote}`);
                    
                    // Reset state and navigate back to home
                    setState({
                        step: 1,
                        images: [],
                        detectedPlate: '',
                        selectedProduct: null,
                        selectedCustomer: null,
                        selectedProject: null,
                        poNumber: '',
                        truckType: null,
                        tareWeight: 0,
                        currentWeight: 0,
                        netWeight: 0,
                        ticketId: null,
                        ticketNum: null,
                        registeredTruckId: null,
                        registeredTruckPlate: null,
                        registeredTruckNumber: null
                    });
                    onNavigate('home');
                } else {
                    alert('Failed to save ticket locally. Please try again.');
                }
                return;
            }

            // ONLINE MODE - Print first, then save to API
            if (!state.ticketId) {
                alert('No ticket ID found. Please try capturing images again.');
                return;
            }

            // console.log('📤 Printing ticket before finalizing...');
            
            // Print ticket immediately using the ticket number from state
            const printSuccess = await printTicket(state.ticketNum, false);

            // Now finalize the ticket with the API
            // console.log('📤 Finalizing ticket with API...');
            const finalizeResponse = await window.electronAPI.request(
                `/companies/${companyData.Id}/tickets/${state.ticketId}`,
                'PATCH',
                finalTicketData,
                {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                }
            );

            if (finalizeResponse.ok) {
                const responseData = await finalizeResponse.body;
                // console.log('✅ Ticket finalized successfully');

                // Update local cache with the new MaxTicketNum from the API
                try {
                    const latestMaxTicketNum = await fetchLatestMaxTicketNum();
                    if (latestMaxTicketNum !== null) {
                        const nextTicketKey = `nextTicketNum_${deviceCode}`;
                        const settings = await window.electronAPI.secureStorage.getSettings() || {};
                        const currentCachedNum = settings[nextTicketKey];
                        const newNextNum = latestMaxTicketNum + 1;
                        
                        // Update to the latest API number if it's higher than our cached number
                        if (!currentCachedNum || newNextNum > currentCachedNum) {
                            settings[nextTicketKey] = newNextNum;
                            await window.electronAPI.secureStorage.setSettings(settings);
                            // console.log(`🔄 Updated local cache: ${currentCachedNum || 'none'} → ${newNextNum} (from API MaxTicketNum: ${latestMaxTicketNum})`);
                        }
                    }
                } catch (error) {
                    console.warn('⚠️ Failed to update local ticket number cache:', error);
                }

                // Update usage counters for customer and product
                try {
                    if (state.selectedCustomer?.Id) {
                        await window.electronAPI.request(
                            `/tickets/${state.ticketId}/customers/${state.selectedCustomer.Id}/usage`,
                            'POST',
                            null,
                            {
                                'Accept': 'application/json',
                                'Content-Type': 'application/json',
                            }
                        );
                    }
                    
                    if (state.selectedProduct?.Id) {
                        await window.electronAPI.request(
                            `/tickets/${state.ticketId}/products/${state.selectedProduct.Id}/usage`,
                            'POST',
                            null,
                            {
                                'Accept': 'application/json',
                                'Content-Type': 'application/json',
                            }
                        );
                    }
                } catch (usageError) {
                    console.warn('⚠️ Failed to update usage counters:', usageError);
                    // Don't fail the ticket creation if usage counter update fails
                }

                const printStatus = printSuccess ? 'and sent to printer!' : 'but printing failed.';
                alert(`Ticket created successfully ${printStatus}`);
            
            // Reset state and navigate back to home
            setState({
                step: 1,
                images: [],
                detectedPlate: '',
                selectedProduct: null,
                selectedCustomer: null,
                selectedProject: null,
                    poNumber: '',
                    truckType: null,
                tareWeight: 0,
                currentWeight: 0,
                    netWeight: 0,
                    ticketId: null,
                    ticketNum: null,
                    registeredTruckId: null,
                    registeredTruckPlate: null,
                    registeredTruckNumber: null
            });
            onNavigate('home');
            } else {
                console.error('❌ Failed to finalize ticket:', finalizeResponse.statusText);
                alert('Failed to finalize ticket. Please try again.');
            }
        } catch (error) {
            console.error('Failed to finalize ticket:', error);
            alert('Failed to finalize ticket. Please try again.');
        }
    };

    const goBack = () => {
        if (state.step > 1) {
            // Clear projects when going back from step 4 to step 3
            if (state.step === 4) {
                setProjects([]);
            }
            setState({ ...state, step: state.step - 1 });
        } else {
            onNavigate('home');
        }
    };

    // Update net weight when current weight changes
    useEffect(() => {
        if (state.step === 5 && state.currentWeight && state.tareWeight) {
            const newNetWeight = Math.max(0, parseFloat(state.currentWeight) - parseFloat(state.tareWeight));
            setState(prev => ({ 
                ...prev, 
                netWeight: newNetWeight
            }));
        }
    }, [state.currentWeight, state.step, state.tareWeight]);

    // Filter functions
    const filteredProducts = products.filter(p =>
        (p?.Name?.toLowerCase() || '').includes(searchTerms.products.toLowerCase()) ||
        (p?.Description?.toLowerCase() || '').includes(searchTerms.products.toLowerCase())
    );

    // Helper functions to get category and unit names by ID
    const getCategoryName = (categoryId) => {
        if (!categoryId || categories.length === 0) return 'Loading...';
        const category = categories.find(c => c.Id === categoryId);
        return category ? category.Name : 'Unknown Category';
    };

    const getUnitName = (unitId) => {
        if (!unitId || units.length === 0) return 'Loading...';
        const unit = units.find(u => u.Id === unitId);
        return unit ? unit.Name : 'Unknown Unit';
    };

    const getCategoryIcon = (categoryId) => {
        if (!categoryId || categories.length === 0) return 'Loading...';
        const category = categories.find(c => c.Id === categoryId);
        return category ? category.Icon : '';
    };

    // Helper function to get product price
    const getProductPrice = (product) => {
        if (product.Price) {
            return product.Price;
        }
        if (product.Pricing && product.Pricing.length > 0) {
            return product.Pricing[0].Price || '0.00';
        }
        return '0.00';
    };

    const filteredCustomers = customers.filter(c =>
        (c?.Name?.toLowerCase() || '').includes(searchTerms.customers.toLowerCase()) ||
        (c?.Phone?.toLowerCase() || '').includes(searchTerms.customers.toLowerCase())
    );

    const availableProjects = projects;

    const filteredProjects = availableProjects.filter(p =>
        (p?.Name?.toLowerCase() || '').includes(searchTerms.projects.toLowerCase()) ||
        (p?.Address?.toLowerCase() || '').includes(searchTerms.projects.toLowerCase())
    );

    const fetchProducts = async () => {
        try {
            setProductsLoading(true);

            // Get saved location from secure storage
            const settings = await window.electronAPI.secureStorage.getSettings();

            if (!settings || !settings.selectedLocation) {
                console.error('❌ No saved location available');
                setProductsLoading(false);
                return;
            }

            const locationId = settings.selectedLocation;
            
            // Get company data from secure storage
            const companyData = await window.electronAPI.secureStorage.getCompany();
            
            if (!companyData || !companyData.Id) {
                console.error('❌ No company data available');
                setProductsLoading(false);
                return;
            }

            // Load cached data first
            const cachedProducts = await window.electronAPI.secureStorage.getCachedProducts();
            if (cachedProducts.data && cachedProducts.data.length > 0) {
                // console.log(`📦 Loading ${cachedProducts.data.length} cached products from ${cachedProducts.timestamp}`);
                setProducts(cachedProducts.data);
                setUsingCachedData(true);
            }

            // Try to fetch fresh data from API if online
            if (isOnline) {
                try {
            // Fetch all products
            const products = await window.electronAPI.request(
                `/companies/${companyData.Id}/locations/${locationId}/products`, 
                'GET', 
                null, 
                {
                    'Accept': 'application/json',
                }
            );

            // Fetch recently used products
            const recentlyUsedResponse = await window.electronAPI.request(
                `/companies/${companyData.Id}/locations/${locationId}/products?order=last_used_at DESC&limit=5`, 
                'GET', 
                null, 
                {
                    'Accept': 'application/json',
                }
            );

            if(products.ok) {
                const productsData = await products.body;
                const actualProducts = productsData.body || productsData;
                        // console.log(`✅ Fetched ${actualProducts.length} products from API`);
                        
                setProducts(actualProducts);
                        setUsingCachedData(false);
                        
                        // Cache the fetched products
                        await window.electronAPI.secureStorage.setCachedProducts(actualProducts);
            } else {
                        console.error('❌ Failed to fetch products from API');
                        if (!cachedProducts.data || cachedProducts.data.length === 0) {
                setProducts(mockProducts);
                        }
            }

            if(recentlyUsedResponse.ok) {
                const recentlyUsedData = await recentlyUsedResponse.body;
                const actualRecentlyUsed = recentlyUsedData.body || recentlyUsedData;
                setRecentlyUsedProducts(actualRecentlyUsed);
            }
        } catch (error) {
                    console.error('❌ Error fetching products from API:', error);
                    // Keep using cached data
                    if (!cachedProducts.data || cachedProducts.data.length === 0) {
            setProducts(mockProducts);
                    }
                }
            } else {
                // Offline - use cached data
                // console.log('📴 Offline - using cached products');
                if (!cachedProducts.data || cachedProducts.data.length === 0) {
                    console.error('❌ No cached products available');
                    alert('No products available offline. Please connect to the internet to load product data.');
                }
            }
        } catch (error) {
            console.error('❌ Error in fetchProducts:', error);
        } finally {
            setProductsLoading(false);
        }
    }

    const fetchCategories = async () => {
        try {
            setCategoriesLoading(true);
            
            // Load cached data first
            const cachedCategories = await window.electronAPI.secureStorage.getCachedCategories();
            if (cachedCategories.data && cachedCategories.data.length > 0) {
                // console.log(`📦 Loading ${cachedCategories.data.length} cached categories`);
                setCategories(cachedCategories.data);
            }

            // Try to fetch from API if online
            if (isOnline) {
                try {
            const categoriesResponse = await window.electronAPI.request(
                `/categories`, 
                'GET', 
                null, 
                {
                    'Accept': 'application/json',
                }
            );

            if(categoriesResponse.ok) {
                const categoriesData = await categoriesResponse.body;
                const actualCategories = categoriesData.body || categoriesData;
                        // console.log(`✅ Fetched ${actualCategories.length} categories from API`);
                        
                setCategories(actualCategories);
                        await window.electronAPI.secureStorage.setCachedCategories(actualCategories);
                    }
                } catch (error) {
                    console.error('❌ Error fetching categories from API:', error);
                }
            } else {
                // console.log('📴 Offline - using cached categories');
            }
        } catch (error) {
            console.error('❌ Error in fetchCategories:', error);
        } finally {
            setCategoriesLoading(false);
        }
    }

    const fetchUnits = async () => {
        try {
            setUnitsLoading(true);
            
            // Load cached data first
            const cachedUnits = await window.electronAPI.secureStorage.getCachedUnits();
            if (cachedUnits.data && cachedUnits.data.length > 0) {
                // console.log(`📦 Loading ${cachedUnits.data.length} cached units`);
                setUnits(cachedUnits.data);
            }

            // Try to fetch from API if online
            if (isOnline) {
                try {
            const unitsResponse = await window.electronAPI.request(
                `/units`, 
                'GET', 
                null, 
                {
                    'Accept': 'application/json',
                }
            );

            if(unitsResponse.ok) {
                const unitsData = await unitsResponse.body;
                const actualUnits = unitsData.body || unitsData;
                        // console.log(`✅ Fetched ${actualUnits.length} units from API`);
                        
                setUnits(actualUnits);
                        await window.electronAPI.secureStorage.setCachedUnits(actualUnits);
                    }
                } catch (error) {
                    console.error('❌ Error fetching units from API:', error);
                }
            } else {
                // console.log('📴 Offline - using cached units');
            }
        } catch (error) {
            console.error('❌ Error in fetchUnits:', error);
        } finally {
            setUnitsLoading(false);
        }
    }

    const fetchTruckTypes = async () => {
        try {
            setTrucktypesLoading(true);
            
            // Load cached data first
            const cachedTruckTypes = await window.electronAPI.secureStorage.getCachedTruckTypes();
            if (cachedTruckTypes.data && cachedTruckTypes.data.length > 0) {
                // console.log(`📦 Loading ${cachedTruckTypes.data.length} cached truck types`);
                setTrucktypes(cachedTruckTypes.data);
            }

            // Try to fetch from API if online
            if (isOnline) {
                try {
            const trucktypesResponse = await window.electronAPI.request(
                `/trucktypes`, 
                'GET', 
                null, 
                {
                    'Accept': 'application/json',
                }
            );

            if(trucktypesResponse.ok) {
                const trucktypesData = await trucktypesResponse.body;
                const actualTruckTypes = trucktypesData.body || trucktypesData;
                        // console.log(`✅ Fetched ${actualTruckTypes.length} truck types from API`);
                        
                setTrucktypes(actualTruckTypes);
                        await window.electronAPI.secureStorage.setCachedTruckTypes(actualTruckTypes);
                    }
                } catch (error) {
                    console.error('❌ Error fetching truck types from API:', error);
                }
            } else {
                // console.log('📴 Offline - using cached truck types');
            }
        } catch (error) {
            console.error('❌ Error in fetchTruckTypes:', error);
        } finally {
            setTrucktypesLoading(false);
        }
    }

    const fetchCustomers = async () => {
        try {
            setCustomersLoading(true);
            
            // Get company data from secure storage
            const companyData = await window.electronAPI.secureStorage.getCompany();
            
            if (!companyData || !companyData.Id) {
                console.error('❌ No company data available for customers');
                setCustomersLoading(false);
                return;
            }

            // Load cached data first
            const cachedCustomers = await window.electronAPI.secureStorage.getCachedCustomers();
            if (cachedCustomers.data && cachedCustomers.data.length > 0) {
                // console.log(`📦 Loading ${cachedCustomers.data.length} cached customers`);
                setCustomers(cachedCustomers.data);
            }

            // Try to fetch from API if online
            if (isOnline) {
                try {
            // Fetch all customers
            const customersResponse = await window.electronAPI.request(
                `/companies/${companyData.Id}/customers`, 
                'GET', 
                null, 
                {
                    'Accept': 'application/json',
                }
            );

            // Fetch recently used customers
            const recentlyUsedResponse = await window.electronAPI.request(
                `/companies/${companyData.Id}/customers?order=last_used_at DESC&limit=5`, 
                'GET', 
                null, 
                {
                    'Accept': 'application/json',
                }
            );

            if(customersResponse.ok) {
                const customersData = await customersResponse.body;
                const actualCustomers = customersData.body || customersData;
                        // console.log(`✅ Fetched ${actualCustomers.length} customers from API`);
                        
                setCustomers(actualCustomers);
                        await window.electronAPI.secureStorage.setCachedCustomers(actualCustomers);
            } else {
                        console.error('❌ Failed to fetch customers from API');
                        if (!cachedCustomers.data || cachedCustomers.data.length === 0) {
                setCustomers(mockCustomers);
                        }
            }

            if(recentlyUsedResponse.ok) {
                const recentlyUsedData = await recentlyUsedResponse.body;
                const actualRecentlyUsed = recentlyUsedData.body || recentlyUsedData;
                setRecentlyUsedCustomers(actualRecentlyUsed);
            }
        } catch (error) {
                    console.error('❌ Error fetching customers from API:', error);
                    if (!cachedCustomers.data || cachedCustomers.data.length === 0) {
            setCustomers(mockCustomers);
                    }
                }
            } else {
                // Offline - use cached data
                // console.log('📴 Offline - using cached customers');
                if (!cachedCustomers.data || cachedCustomers.data.length === 0) {
                    console.error('❌ No cached customers available');
                    alert('No customers available offline. Please connect to the internet to load customer data.');
                }
            }
        } catch (error) {
            console.error('❌ Error in fetchCustomers:', error);
        } finally {
            setCustomersLoading(false);
        }
    }

    const fetchProjects = async (customerId) => {
        try {
            setProjectsLoading(true);
            
            if (!customerId) {
                console.error('❌ No customer ID provided for projects');
                setProjectsLoading(false);
                return;
            }

            // Load cached projects first
            const cachedProjects = await window.electronAPI.secureStorage.getCachedProjects(customerId);
            if (cachedProjects.data && cachedProjects.data.length > 0) {
                // console.log(`📦 Loading ${cachedProjects.data.length} cached projects for customer ${customerId}`);
                setProjects(cachedProjects.data);
            }

            // Try to fetch from API if online
            if (isOnline) {
                try {
            const projectsResponse = await window.electronAPI.request(
                `/customers/${customerId}/projects`, 
                'GET', 
                null, 
                {
                    'Accept': 'application/json',
                }
            );

            if(projectsResponse.ok) {
                const projectsData = await projectsResponse.body;
                const actualProjects = projectsData.body || projectsData;
                        // console.log(`✅ Fetched ${actualProjects.length} projects from API for customer ${customerId}`);
                        
                setProjects(actualProjects);
                        await window.electronAPI.secureStorage.setCachedProjects(customerId, actualProjects);
            } else {
                        console.error('❌ Failed to fetch projects from API');
                        if (!cachedProjects.data || cachedProjects.data.length === 0) {
                setProjects([]);
                        }
            }
        } catch (error) {
                    console.error('❌ Error fetching projects from API:', error);
                    if (!cachedProjects.data || cachedProjects.data.length === 0) {
                        setProjects([]);
                    }
                }
            } else {
                // Offline - use cached data
                // console.log('📴 Offline - using cached projects');
                if (!cachedProjects.data || cachedProjects.data.length === 0) {
                    // console.log('⚠️ No cached projects for this customer');
                    setProjects([]);
                }
            }
        } catch (error) {
            console.error('❌ Error in fetchProjects:', error);
            setProjects([]);
        } finally {
            setProjectsLoading(false);
        }
    }

    const fetchRegisteredTrucks = async () => {
        try {
            // Only fetch if online
            if (!isOnline) {
                // console.log('📴 Offline - skipping registered trucks fetch');
                return;
            }

            const companyData = await window.electronAPI.secureStorage.getCompany();
            
            if (!companyData || !companyData.Id) {
                // console.log('❌ No company data available for trucks');
                return;
            }

            // Fetch all registered trucks
            const trucksResponse = await window.electronAPI.request(
                `/companies/${companyData.Id}/registeredtrucks`, 
                'GET', 
                null, 
                {
                    'Accept': 'application/json',
                }
            );

            if(trucksResponse.ok) {
                const trucksData = await trucksResponse.body;

                // console.log(trucksData);
                const actualTrucks = trucksData.body || trucksData;
                // console.log(`✅ Fetched ${actualTrucks.length} registered trucks from API`);
                
                // Merge with pending trucks for complete cache
                const pendingTrucks = await window.electronAPI.secureStorage.getPendingTrucks();
                
                // Convert pending trucks to look like API trucks
                const pendingAsTrucks = pendingTrucks.map(t => ({
                    Id: t.localId,
                    NumberPlate: t.numberplate,
                    TruckNumber: t.trucknumber,
                    TareWeight: t.tareweight,
                    DOTNumber: t.dotnumber,
                    CompanyName: t.companyname,
                    Status: 'pending_sync'
                }));

                const allTrucks = [...actualTrucks, ...pendingAsTrucks];
                await window.electronAPI.secureStorage.setCachedRegisteredTrucks(allTrucks);
                // console.log(`📦 Cached ${allTrucks.length} total trucks (${actualTrucks.length} synced + ${pendingTrucks.length} pending)`);
            } else {
                console.error('❌ Failed to fetch registered trucks');
            }
        } catch (error) {
            console.error('❌ Error fetching registered trucks:', error);
        }
    }

    return (
        <div className="screen">
            <div className="nav-breadcrumb">
                <a onClick={() => onNavigate('home')}>Home</a> / Create Ticket
            </div>

            {/* Offline Mode Indicator */}
            {!isOnline && (
                <div style={{
                    background: '#ff6b6b',
                    color: 'white',
                    padding: '0.75rem 1rem',
                    borderRadius: '8px',
                    marginBottom: '1rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                }}>
                    <i className="fas fa-wifi" style={{ textDecoration: 'line-through' }}></i>
                    <strong>Offline Mode</strong> - Using cached data. Tickets will be saved locally and synced when connection is restored.
                </div>
            )}

            {/* Cached Data Warning */}
            {isOnline && usingCachedData && (
                <div style={{
                    background: '#fff3cd',
                    color: '#856404',
                    padding: '0.75rem 1rem',
                    borderRadius: '8px',
                    marginBottom: '1rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                }}>
                    <i className="fas fa-info-circle"></i>
                    <span>Using cached data. Fresh data loaded in background.</span>
                </div>
            )}

            <WizardSteps 
                steps={steps} 
                currentStep={state.step} 
                state={state} 
                setState={setState} 
            />

            {state.step === 1 && (
                <div className="ticket-screen-capture">
                    <div className="weight-display scale" id="weightReading" style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        padding: '1rem',
                        background: '#333',
                        borderRadius: '8px',
                        border: '1px solid #e9ecef',
                        margin: '0'
                    }}>
                        <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.1rem', color: 'inherit' }}><i className="fas fa-weight-hanging"></i> Live Scale Reading</h3>
                        {currentScaleData.weight ? (
                            <div style={{
                                fontSize: '2.5rem',
                                fontWeight: 'bold',
                                height: '50px',
                                textShadow: '0 2px 4px rgba(0,0,0,0.3)'
                            }}>
                                {currentScaleData.weight}
                                <span style={{
                                    fontSize: '1rem',
                                    opacity: 0.8,
                                    className: currentScaleData.status === 'unstable' ? 'unstable' : ''
                                }}>
                                </span>
                            </div>
                        ) : (
                            <div style={{
                                fontSize: '1.5rem',
                                opacity: 0.7,
                                height: '50px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontStyle: 'italic'
                            }}>
                                Waiting for weight data...
                            </div>
                        )}
                        <div style={{
                            fontSize: '0.875rem',
                            opacity: 0.8,
                            marginTop: '0.5rem'
                        }}>
                            {currentScaleData.status === 'unstable' ? 'Weight unstable - please wait for stable reading' : 'Weight stable'}
                            {isDualScaleMode && currentScaleData.scaleType && (
                                <span style={{ marginLeft: '0.5rem', fontSize: '0.75rem', opacity: 0.6 }}>
                                    ({currentScaleData.scaleType === 'empty' ? 'Empty Scale' : 'Full Scale'})
                                </span>
                            )}
                        </div>
                    </div>


                    {/* Front Camera (Primary) */}
                    {primaryCamera && primaryCamera.id ? (
                        <div
                            key={primaryCamera?.id}
                            className="cam1">
                            <div
                                style={{
                                    width: '100%',
                                    // height: '100%',
                                    border: '2px solid #e2e8f0',
                                    borderRadius: '8px',
                                    overflow: 'hidden',
                                    background: '#f8f9fa',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    position: 'relative',
                                }}
                            >
                                <img
                                    data-camera-id={primaryCamera?.id}
                                    src=""
                                    alt={`Camera ${primaryCamera?.name}`}
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'contain'
                                    }}
                                />
                                <div className="camera-overlay" style={{
                                    position: 'absolute',
                                    bottom: '0',
                                    left: '0',
                                    right: '0',
                                    background: 'rgba(0,0,0,0.7)',
                                    color: 'white',
                                    padding: '0.75rem',
                                    fontSize: '0.875rem'
                                }}>
                                    {`${primaryCamera?.name} - Primary` || `Camera ${primaryCamera?.id}`}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="cam1" style={{
                            width: '100%',
                            border: '2px dashed #6c757d',
                            borderRadius: '8px',
                            overflow: 'hidden',
                            background: '#f8f9fa',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            position: 'relative',
                            minHeight: '200px'
                        }}>
                            <div style={{ textAlign: 'center', color: '#6c757d' }}>
                                <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}><i className="fas fa-video-slash"></i></div>
                                <div><strong>No Camera Configured</strong></div>
                                <div style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>
                                    Camera is optional - you can still create tickets manually
                                </div>
                            </div>
                        </div>
                    )}

                    {/* </div> */}
                    {/* Additional Cameras in 2x2 Grid */}
                    {configuredCameras.length > 0 && (
                        configuredCameras.map((camera, index) => {
                            const camNum = index + 2;
                            return (
                                <div
                                    key={camera.id}
                                    className={`cam${camNum}`}
                                    style={{
                                        background: '#808080',
                                        borderRadius: '8px',
                                        border: '2px solid #e2e8f0',
                                        alignContent: 'center',
                                        overflow: 'hidden'
                                    }}
                                >
                                    <div
                                        style={{
                                            width: '100%',
                                            overflow: 'hidden',
                                            background: '#f8f9fa',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            position: 'relative',
                                        }}
                                    >
                                        <img
                                            data-camera-id={camera.id}
                                            src=""
                                            alt={`Camera ${camera.name}`}
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'contain',
                                            }}
                                        />
                                        <div
                                            className="camera-overlay"
                                            style={{
                                                position: 'absolute',
                                                bottom: '0',
                                                left: '0',
                                                right: '0',
                                                background: 'rgba(0,0,0,0.7)',
                                                color: 'white',
                                                padding: '0.75rem',
                                                fontSize: '0.875rem',
                                            }}
                                        >
                                            {camera.name || `Camera ${camera.id}`}
                                        </div>
                                    </div>
                                </div>
                            )
                        })
                    )}

                    <div className='guide'>
                        <div>
                            <h4>Instructions:</h4>
                            <ul style={{ fontSize: '0.875rem', color: '#4a5568', paddingLeft: '1.5rem' }}>
                                <li>Ensure the vehicle is properly positioned on the scale</li>
                                <li>Wait for the weight reading to stabilize</li>
                                {primaryCamera || configuredCameras.length > 0 ? (
                                    <>
                                <li>Make sure all cameras have a clear view</li>
                                <li>Click "Capture All Images" when ready</li>
                                    </>
                                ) : (
                                    <li>Click "Continue to Product Selection" to proceed (no cameras configured)</li>
                                )}
                            </ul>
                        </div>
                    </div>

                    <div className='btn-cell'>
                        <button
                            className="btn btn-success btn-large"
                            onClick={captureImages}
                            disabled={!devices.scale.connected}
                        >
                            {primaryCamera || configuredCameras.length > 0 ? (
                                <><i className="fas fa-camera"></i> Capture All Images</>
                            ) : (
                                <><i className="fas fa-arrow-right"></i> Continue to Product Selection</>
                            )}
                        </button>
                    </div>
                </div>
            )}

            {state.step === 2 && (
                <div>
                    <div className="recently-used">
                        <h3>Recently Used Products</h3>
                        <div className="product-grid">
                            {recentlyUsedProducts.length > 0 ? (
                                recentlyUsedProducts.map(product => {
                                    // Find the first image file for this product
                                    const productImage = product.Files?.find(file => file.Type === 'image');
                                    
                                    return (
                                        <div
                                            key={product.Id}
                                    className="product-item"
                                    onClick={() => selectProduct(product)}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '0.75rem',
                                                padding: '0.75rem',
                                                border: '1px solid #e2e8f0',
                                                borderRadius: '8px',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s ease',
                                                backgroundColor: '#fff'
                                            }}
                                        >
                                            {/* Product Image - Left Third */}
                                            {productImage?.ThumbUrl && (
                                                <div style={{
                                                    flexShrink: 0,
                                                    width: '33.33%',
                                                    height: '100%',
                                                    position: 'relative',
                                                }}>
                                                    <img
                                                        src={productImage.ThumbUrl}
                                                        alt={product.Name}
                                                        style={{
                                                            width: '100%',
                                                            height: '100%',
                                                            objectFit: 'cover',
                                                            borderRadius: '8px',
                                                        }}
                                                        onError={(e) => {
                                                            e.target.style.display = 'none';
                                                        }}
                                                    />
                                                </div>
                                            )}
                                            
                                            {/* Product Details */}
                                            <div style={{ 
                                                flex: 1, 
                                                minWidth: 0,
                                                paddingLeft: productImage?.ThumbUrl ? '0.75rem' : '0'
                                            }}>
                                                <span>{ getCategoryIcon(product.CategoryId) }</span>
                                                <strong style={{ 
                                                    display: 'block', 
                                                    marginBottom: '0.25rem',
                                                    fontSize: '0.9rem',
                                                    color: '#2d3748'
                                                }}>
                                                    {product.Name}
                                                </strong>
                                                <p style={{ 
                                                    margin: '0 0 0.5rem 0',
                                                    fontSize: '0.8rem',
                                                    color: '#4a5568',
                                                    lineHeight: '1.3'
                                                }}>
                                                    {product.Description}
                                                </p>
                                                <small style={{ 
                                                    color: '#718096',
                                                    fontSize: '0.75rem',
                                                    display: 'block'
                                                }}>
                                                    {getCategoryName(product.CategoryId)} • ${getProductPrice(product)}/{getUnitName(product.UnitId)}
                                    </small>
                                </div>
                                        </div>
                                    );
                                })
                            ) : (
                                <p style={{ color: '#718096', fontStyle: 'italic' }}>
                                    No recently used products
                                </p>
                            )}
                        </div>
                    </div>
                    
                    <div className="search-box">
                        <input 
                            className="form-input" 
                            placeholder="Search products..."
                            value={searchTerms.products}
                            autoFocus
                            onChange={(e) => setSearchTerms({...searchTerms, products: e.target.value})}
                        />
                    </div>
                    
                    <div className="product-grid">
                        {productsLoading || categoriesLoading || unitsLoading ? (
                            <div className="loading-indicator">
                                <p>Loading products...</p>
                            </div>
                        ) : filteredProducts.length > 0 ? (
                            filteredProducts.map(product => {
                                // Find the first image file for this product
                                const productImage = product.Files?.find(file => file.Type === 'image');
                                
                                return (
                                <div
                                    key={product.id}
                                    className="product-item"
                                    onClick={() => selectProduct(product)}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.75rem',
                                            padding: '0.75rem',
                                            border: '1px solid #e2e8f0',
                                            borderRadius: '8px',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease',
                                            backgroundColor: '#fff'
                                        }}
                                    >
                                        {/* Product Image - Left Third */}
                                        {productImage?.ThumbUrl && (
                                            <div style={{
                                                flexShrink: 0,
                                                width: '33.33%',
                                                height: '100%',
                                                position: 'relative',
                                            }}>
                                                <img
                                                    src={productImage.ThumbUrl}
                                                    alt={product.Name}
                                                    style={{
                                                        width: '100%',
                                                        height: '100%',
                                                        objectFit: 'cover',
                                                        borderRadius: '8px',
                                                    }}
                                                    onError={(e) => {
                                                        e.target.style.display = 'none';
                                                    }}
                                                />
                                            </div>
                                        )}
                                        
                                        {/* Product Details */}
                                        <div style={{ 
                                            flex: 1, 
                                            minWidth: 0,
                                            paddingLeft: productImage?.ThumbUrl ? '0.75rem' : '0'
                                        }}>
                                            <span>{ getCategoryIcon(product.CategoryId) }</span>
                                            <strong style={{ 
                                                display: 'block', 
                                                marginBottom: '0.25rem',
                                                fontSize: '0.9rem',
                                                color: '#2d3748'
                                            }}>
                                                {product.Name}
                                            </strong>
                                            <p style={{ 
                                                margin: '0 0 0.5rem 0',
                                                fontSize: '0.8rem',
                                                color: '#4a5568',
                                                lineHeight: '1.3'
                                            }}>
                                                {product.Description}
                                            </p>
                                            <small style={{ 
                                                color: '#718096',
                                                fontSize: '0.75rem',
                                                display: 'block'
                                            }}>
                                        {getCategoryName(product.CategoryId)} • ${getProductPrice(product)}/{getUnitName(product.UnitId)}
                                    </small>
                                </div>
                                    </div>
                                );
                            })
                        ) : (
                            <div className="no-products">
                                <p>No products found. Please check your connection or try again.</p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {state.step === 3 && (
                <div>
                    <div className="recently-used">
                        <h3>Recently Used Customers</h3>
                        <div className="product-grid">
                            {recentlyUsedCustomers.length > 0 ? (
                                recentlyUsedCustomers.map(customer => (
                                <div
                                        key={customer.Id}
                                    className="product-item"
                                    onClick={() => selectCustomer(customer)}
                                >
                                        <strong>{customer.Name}</strong>
                                        <p>Phone: {customer.Phone}</p>
                                    <small style={{ color: '#718096' }}>
                                            Customer ID: {customer.Id}
                                    </small>
                                </div>
                                ))
                            ) : (
                                <p style={{ color: '#718096', fontStyle: 'italic' }}>
                                    No recently used customers
                                </p>
                            )}
                        </div>
                    </div>
                    
                    <div className="search-box">
                        <input 
                            className="form-input" 
                            placeholder="Search customers..." 
                            value={searchTerms.customers}
                            autoFocus
                            onChange={(e) => setSearchTerms({...searchTerms, customers: e.target.value})}
                        />
                    </div>
                    
                    <div className="product-grid">
                        {customersLoading ? (
                            <div className="loading-indicator">
                                <p>Loading customers...</p>
                            </div>
                        ) : filteredCustomers.length > 0 ? (
                            filteredCustomers.map(customer => (
                                <div
                                    key={customer.Id}
                                    className="product-item"
                                    onClick={() => selectCustomer(customer)}
                                >
                                    <strong>{customer.Name}</strong>
                                    <p>Phone: {customer.Phone}</p>
                                    <small style={{ color: '#718096' }}>
                                        Customer ID: {customer.Id}
                                    </small>
                                </div>
                            ))
                        ) : (
                            <div className="no-products">
                                <p>No customers found. Please check your connection or try again.</p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {state.step === 4 && (
                <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2rem', alignItems: 'center' }}>
                        <p style={{ color: '#718096' }}>
                            Projects for: <strong>{state.selectedCustomer?.Name}</strong>
                        </p>

                        <button
                            className="btn btn-success"
                            onClick={() => {
                                const netWeight = Math.max(0, state.currentWeight - state.tareWeight);
                                setState({
                                    ...state,
                                    selectedProject: null,
                                    step: 5,
                                    netWeight
                                });
                            }}
                        >
                            Skip <i className='fa fa-angle-right'></i>
                        </button>
                    </div>
                    
                    {projectsLoading ? (
                        <div className="loading-indicator">
                            <p>Loading projects...</p>
                        </div>
                    ) : availableProjects.length === 0 ? (
                        <div className="alert alert-warning">
                            <p>No projects found for this customer. Please contact your administrator to set up projects.</p>
                        </div>
                    ) : (
                        <>
                            <div className="search-box">
                                <input 
                                    className="form-input" 
                                    placeholder="Search projects..." 
                                    value={searchTerms.projects}
                                    autoFocus
                                    onChange={(e) => setSearchTerms({...searchTerms, projects: e.target.value})}
                                />
                            </div>
                            
                            <div className="product-grid projects">
                                {filteredProjects.map(project => (
                                    <div
                                        key={project.Id}
                                        className="product-item"
                                        onClick={() => selectProject(project)}
                                    >
                                        <strong>{project.Name}</strong>
                                        <p style={{ fontSize: '0.875rem', margin: '0.5rem 0' }}>
                                            {project.Address}
                                        </p>
                                        <small style={{ color: '#718096' }}>
                                            Project ID: {project.Id}
                                        </small>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            )}

            {state.step === 5 && (
                <div>
                    <div className="grid-2">
                        <div>
                            <h3>Ticket Summary</h3>
                            <div className="summary-table">
                                <div className="summary-row">
                                    <span>#️⃣ License Plate:</span>
                                    <span>
                                        {state.isProcessing ? (
                                            <div style={{ 
                                                display: 'flex', 
                                                alignItems: 'center', 
                                                gap: '0.5rem',
                                                minWidth: '120px'
                                            }}>
                                                <div className="loading-spinner" style={{
                                                    width: '1rem',
                                                    height: '1rem',
                                                    border: '2px solid #e2e8f0',
                                                    borderTop: '2px solid #667eea',
                                                    borderRadius: '50%',
                                                    animation: 'spin 1s linear infinite'
                                                }}></div>
                                                <span style={{ fontSize: '0.875rem', color: '#667eea' }}>Processing...</span>
                                            </div>
                                        ) : (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                        <input
                                            className="form-input"
                                            value={state.detectedPlate}
                                                    onChange={(e) => {
                                                        const newPlate = e.target.value.toUpperCase();
                                                        setState({...state, detectedPlate: newPlate});
                                                        // Track if user manually changed the plate
                                                        if (newPlate !== state.detectedPlate) {
                                                            setPlateManuallyChanged(true);
                                                            // Clear truck ID when plate changes
                                                            setState(prev => ({
                                                                ...prev,
                                                                detectedPlate: newPlate,
                                                                registeredTruckId: null
                                                            }));
                                                        }
                                                    }}
                                            style={{ 
                                                width: 'auto', 
                                                display: 'inline', 
                                                padding: '0.25rem',
                                                minWidth: '120px',
                                                fontWeight: 'bold',
                                                textAlign: 'center'
                                            }}
                                        />
                                                <button
                                                    className="btn btn-secondary btn-small"
                                                    onClick={updateTruckInfo}
                                                    disabled={!state.detectedPlate.trim() || state.isUpdatingTruck}
                                                    style={{ 
                                                        fontSize: '0.75rem', 
                                                        padding: '0.25rem 0.5rem',
                                                        minWidth: 'auto'
                                                    }}
                                                >
                                                    {state.isUpdatingTruck ? (
                                                        <>
                                                            <i className="fas fa-spinner fa-spin"></i> Updating...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <i className="fas fa-sync"></i> Update Truck
                                                        </>
                                                    )}
                                                </button>
                                            </div>
                                        )}
                                    </span>
                                </div>
                                <div className="summary-row">
                                    <span>🚛 Truck Number:</span>
                                    <span>
                                        {state.isProcessing ? (
                                            <div style={{ 
                                                display: 'flex', 
                                                alignItems: 'center', 
                                                gap: '0.5rem',
                                                minWidth: '120px'
                                            }}>
                                                <div className="loading-spinner" style={{
                                                    width: '1rem',
                                                    height: '1rem',
                                                    border: '2px solid #e2e8f0',
                                                    borderTop: '2px solid #667eea',
                                                    borderRadius: '50%',
                                                    animation: 'spin 1s linear infinite'
                                                }}></div>
                                                <span style={{ fontSize: '0.875rem', color: '#667eea' }}>Processing...</span>
                                            </div>
                                        ) : (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                {state.registeredTruckNumber} <a href={`https://aggmaps.com/portal/registeredtruck?id=${state.registeredTruckId}`}></a>
                                            </div>
                                        )}
                                    </span>
                                </div>
                                {!state.isProcessing && state.detectedPlate && (!state.registeredTruckId || plateManuallyChanged) && (
                                    <div className="summary-row" style={{ marginTop: '0.5rem' }}>
                                        <span></span>
                                        <span style={{ color: '#dc3545', fontSize: '0.875rem' }}>
                                            {plateManuallyChanged ?
                                                <><i className="fas fa-exclamation-triangle"></i> License plate changed. Click "Update Truck" to verify the new plate.</> :
                                                <><i className="fas fa-exclamation-triangle"></i> No truck found with this license plate. Click "Update Truck" to search.</>
                                            }
                                        </span>
                                    </div>

                                )}
                                <div className="summary-row">
                                    <span>🪨 Product:</span>
                                    <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                        {state.selectedProduct?.Files?.find(file => file.type === 'image')?.ThumbUrl ? (
                                            <img
                                                src={state.selectedProduct.Files.find(file => file.type === 'image').ThumbUrl}
                                                alt={state.selectedProduct.Name}
                                                style={{
                                                    width: '24px',
                                                    height: '24px',
                                                    borderRadius: '4px',
                                                    objectFit: 'cover',
                                                    border: '1px solid #e2e8f0'
                                                }}
                                                onError={(e) => {
                                                    e.target.style.display = 'none';
                                                }}
                                            />
                                        ) : null}
                                    <span>{ getCategoryIcon(state.selectedProduct?.CategoryId) } {state.selectedProduct?.Name}</span>
                                    </span>
                                </div>
                                <div className="summary-row">
                                    <span>🧑 Customer:</span>
                                    <span>{state.selectedCustomer?.Name}</span>
                                </div>
                                <div className="summary-row">
                                    <span>🚧 Project:</span>
                                    <span>{state.selectedProject?.Name || 'None'}</span>
                                </div>
                                <div className="summary-row">
                                    <span>#️⃣ PO Number:</span>
                                    <span>
                                        <input
                                            className="form-input"
                                            placeholder="(optional)"
                                            value={state.poNumber || ''}
                                            onChange={(e) => setState({...state, poNumber: e.target.value})}
                                            style={{ 
                                                width: 'auto', 
                                                display: 'inline', 
                                                padding: '0.25rem',
                                                minWidth: '150px',
                                                fontSize: '0.875rem'
                                            }}
                                        />
                                    </span>
                                </div>
                                
                                { (state.selectedProduct?.UnitId == 2) && (
                                    <div className="summary-row">
                                        <span>🚛 Truck Type:</span>
                                        <span>
                                            <select className="form-input" value={state.truckType || ''} onChange={(e) => setState({...state, truckType: e.target.value})}>
                                                {trucktypes.map(trucktype => (
                                                    <option key={trucktype.Id} value={trucktype.Id}>{trucktype.Name}</option>
                                                ))}
                                            </select>
                                        </span>
                                    </div>
                                )}

                                <div className="summary-row">
                                    <span>⚖️ Gross Weight:</span>
                                    <span>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                            <span>{state.currentWeight ? (isNaN(state.currentWeight) ? `${state.currentWeight.toLocaleString()}` : `${state.currentWeight.toLocaleString()} Tons`) : '0.00 Tons'}</span>
                                            <button
                                                className="btn btn-transparent btn-small"
                                                onClick={() => {
                                                    if (currentScaleData.weight) {
                                                        let numericWeight = 0;
                                                        if (typeof currentScaleData.weight === 'string') {
                                                            numericWeight = parseFloat(currentScaleData.weight.replace(/[^\d.-]/g, '')).toFixed(2) || 0.00;
                                                        } else {
                                                            numericWeight = parseFloat(currentScaleData.weight).toFixed(2) || 0.00;
                                                        }
                                                        const adjustedWeight = numericWeight;
                                                        setState(prev => ({
                                                            ...prev,
                                                            currentWeight: adjustedWeight,
                                                            netWeight: Math.max(0, adjustedWeight - prev.tareWeight)
                                                        }));
                                                    }
                                                }}
                                                style={{ 
                                                    fontSize: '0.75rem', 
                                                    padding: '0.25rem 0.5rem',
                                                    minWidth: 'auto'
                                                }}
                                                title="Update with current scale reading"
                                            >
                                                <i className="fas fa-sync"></i>
                                            </button>
                                        </div>
                                    </span>
                                </div>
                                <div className="summary-row">
                                    <span>⚖️ Tare Weight:</span>
                                    <span>
                                        {state.isProcessing ? (
                                            <div style={{ 
                                                display: 'flex', 
                                                alignItems: 'center', 
                                                gap: '0.5rem'
                                            }}>
                                                <div className="loading-spinner" style={{
                                                    width: '1rem',
                                                    height: '1rem',
                                                    border: '2px solid #e2e8f0',
                                                    borderTop: '2px solid #667eea',
                                                    borderRadius: '50%',
                                                    animation: 'spin 1s linear infinite'
                                                }}></div>
                                                <span style={{ fontSize: '0.875rem', color: '#667eea' }}>Loading...</span>
                                            </div>
                                        ) : (
                                            `${state.tareWeight.toLocaleString()} Tons`
                                        )}
                                    </span>
                                </div>
                                <div className="summary-row">
                                    <span>⚖️ Net Weight:</span>
                                    <span style={{ fontWeight: 'bold', color: '#48bb78' }}>
                                        {state.currentWeight && state.tareWeight ? 
                                            `${state.netWeight.toFixed(2)} Tons` : 
                                            '0.00 Tons'
                                        }
                                    </span>
                                </div>
                            </div>
                            
                            {state.selectedProduct && (
                                <div style={{ marginTop: '1rem', padding: '1rem', background: '#f8f9fa', borderRadius: '8px' }}>
                                    <h4>💲 Estimated Cost</h4>
                                    <p style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#48bb78' }}>
                                        ${state.currentWeight && state.tareWeight ? 
                                            (state.netWeight * parseFloat(getProductPrice(state.selectedProduct))).toFixed(2) : 
                                            '0.00'
                                        }
                                    </p>
                                    <small style={{ color: '#718096' }}>
                                        {state.currentWeight && state.tareWeight ? 
                                            `${state.netWeight.toFixed(2)} Tons × $${getProductPrice(state.selectedProduct)}/${getUnitName(state.selectedProduct.UnitId)}` : 
                                            '0.00 Tons × $0.00/Ton'
                                        }
                                    </small>
                                </div>
                            )}
                        </div>
                        
                        <div>
                            <div className="weight-display" style={{ 
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                padding: '1rem',
                                background: '#333',
                                borderRadius: '8px',
                                border: '1px solid #e9ecef',
                                margin: '0'
                            }}>
                                <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.1rem', color: 'inherit' }}><i className="fas fa-weight-hanging"></i> Live Scale Reading</h3>
                                {currentScaleData.weight ? (
                                    <div style={{ 
                                        fontSize: '2.5rem',
								        fontWeight: 'bold',
								        height: '50px',
								        textShadow: '0 2px 4px rgba(0,0,0,0.3)'
                                    }}>
                                        {currentScaleData.weight}
                                        <span style={{ 
                                            fontSize: '0.875rem',
                                            opacity: 0.8
                                        }}>
                                            {/* {weightStatus === 'unstable' ? ' ⚠️' : ' ✅'} */}
                                        </span>
                                    </div>
                                ) : (
                                    <div style={{ 
                                        fontSize: '1.5rem',
								        opacity: 0.7,
								        height: '50px',
								        display: 'flex',
								        alignItems: 'center',
								        justifyContent: 'center',
								        fontStyle: 'italic'
                                    }}>
                                        Waiting for weight data...
                                    </div>
                                )}
                                <div style={{ 
                                    fontSize: '0.875rem', 
                                    opacity: 0.8,
                                    marginTop: '0.5rem'
                                }}>
                                    {weightStatus === 'unstable' ? 'Weight unstable' : 'Weight stable'}
                                </div>
                            </div>
                            <p style={{ color: '#718096', textAlign: 'center', marginTop: '1rem' }}>
                                Live weight reading • Updates in real-time
                            </p>
                            
                            <div style={{ marginTop: '2rem' }}>
                                <h4>Captured Images</h4>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '2rem' }}>
                                    {state.images.map(image => (
                                        <div key={image.id} style={{ textAlign: 'center' }}>
                                            <div style={{
                                                background: '#f5f5f5',
                                                borderRadius: '8px',
                                                overflow: 'hidden',
                                                border: '2px solid #e2e8f0',
                                                minHeight: '120px',
                                                position: 'relative'
                                            }}>
                                                <img 
                                                    src={image.url}
                                                    alt={`${image.camera} Capture`}
                                                    style={{
                                                        width: '100%',
                                                        height: '100%',
                                                        objectFit: 'cover',
                                                        minHeight: '120px'
                                                    }}
                                                />
                                                <div style={{
                                                    position: 'absolute',
                                                    bottom: '0',
                                                    left: '0',
                                                    right: '0',
                                                    background: 'rgba(0,0,0,0.7)',
                                                    color: 'white',
                                                    padding: '0.25rem',
                                                    fontSize: '0.75rem'
                                                }}>
                                                    									<i className="fas fa-camera"></i> {new Date(image.timestamp).toLocaleTimeString()}
                                                </div>
                                            </div>
                                            <small style={{ display: 'block', marginTop: '0.5rem', color: '#718096' }}>
                                                {image.camera}
                                            </small>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="wizard-actions">
                <button className="btn btn-secondary" onClick={goBack}>
                    {state.step === 1 ? 'Cancel' : 'Back'}
                </button>
                
                {state.step === 5 && (
                    <button
                        className="btn btn-success"
                        onClick={finalizeTicket}
                        disabled={!state.detectedPlate.trim() || !state.currentWeight || !state.tareWeight || 
                            state.netWeight <= 0 || state.isProcessing || !state.registeredTruckId || plateManuallyChanged}
                    >
                        						{state.isProcessing ? (
							<><i className="fas fa-spinner fa-spin"></i> Processing...</>
						) : isOnline ? (
							<><i className="fas fa-print"></i> Print & Save Ticket</>
						) : (
							<><i className="fas fa-print"></i> Print & Save Offline</>
						)}
                    </button>
                )}
            </div>
        </div>
    );
};

export default CreateTicketScreen;